<template>
  <div class="tw-h-full tw-flex tw-flex-col tw-bg-gray-50">
    <!-- Compact Participants Header -->
    <div class="tw-p-2 tw-border-b tw-border-gray-200 tw-bg-white">
      <h3 class="tw-text-sm tw-font-semibold tw-text-gray-900">Participants</h3>
      <p class="tw-text-xs tw-text-gray-600 tw-mt-0.5">
        {{ participants.length }} participant{{ participants.length !== 1 ? 's' : '' }}
      </p>
    </div>

    <!-- Compact Session Info -->
    <div v-if="session" class="tw-p-2 tw-border-b tw-border-gray-200 tw-bg-white">
      <div class="tw-flex tw-items-center tw-gap-2 tw-mb-2">
        <div class="tw-w-6 tw-h-6 tw-rounded-full tw-bg-primary-100 tw-flex tw-items-center tw-justify-center">
          <icon icon="solar:chat-round-dots-bold-duotone" class="tw-text-primary-600 tw-text-xs" />
        </div>
        <div>
          <h4 class="tw-text-sm tw-font-medium tw-text-gray-900">{{ session.title }}</h4>
          <p class="tw-text-xs tw-text-gray-500">Session Chat</p>
        </div>
      </div>
      
      <div class="tw-flex tw-items-center tw-gap-2 tw-text-xs tw-text-gray-500">
        <span class="tw-flex tw-items-center tw-gap-1">
          <icon icon="solar:calendar-bold" class="tw-text-gray-400 tw-text-xs" />
          {{ formatSessionDate(session.start_date, session.start_time) }}
        </span>
        <span class="tw-flex tw-items-center tw-gap-1">
          <icon icon="solar:clock-circle-bold" class="tw-text-gray-400 tw-text-xs" />
          {{ formatDuration(session.start_time, session.end_time) }}
        </span>
      </div>
    </div>

    <!-- Participants List -->
    <div class="tw-flex-1 tw-overflow-y-auto tw-p-2">
      <!-- Loading State -->
      <div v-if="loading" class="tw-space-y-2">
        <div v-for="i in 5" :key="i" class="tw-flex tw-items-center tw-gap-2 tw-p-2 tw-bg-white tw-rounded-lg">
          <div class="tw-w-6 tw-h-6 tw-bg-gray-200 tw-rounded-full tw-animate-pulse"></div>
          <div class="tw-flex-1 tw-space-y-1">
            <div class="tw-h-3 tw-bg-gray-200 tw-rounded tw-w-20 tw-animate-pulse"></div>
            <div class="tw-h-2 tw-bg-gray-200 tw-rounded tw-w-24 tw-animate-pulse"></div>
          </div>
        </div>
      </div>

      <!-- Participants -->
      <div v-else-if="participants.length > 0" class="tw-space-y-1">
        <div
          v-for="participant in participants"
          :key="participant.id"
          class="tw-flex tw-items-center tw-gap-2 tw-p-2 tw-bg-white tw-rounded-lg hover:tw-bg-gray-50 tw-transition-colors"
        >
          <!-- Participant Avatar -->
          <div class="tw-w-6 tw-h-6 tw-rounded-full tw-flex-shrink-0 tw-overflow-hidden">
            <img 
              v-if="participant.profile_image" 
              :src="participant.profile_image" 
              :alt="participant.name"
              class="tw-w-full tw-h-full tw-object-cover"
            />
            <div v-else class="tw-w-full tw-h-full tw-bg-primary-100 tw-flex tw-items-center tw-justify-center">
              <icon icon="solar:user-bold-duotone" class="tw-text-primary-600 tw-text-xs" />
            </div>
          </div>
          
          <!-- Participant Info -->
          <div class="tw-flex-1 tw-min-w-0">
            <div class="tw-flex tw-items-center tw-gap-1">
              <h4 class="tw-text-xs tw-font-medium tw-text-gray-900 tw-truncate">
                {{ participant.name }}
              </h4>
              
              <!-- Online Status Indicator -->
              <div 
                v-if="participant.is_online"
                class="tw-w-1.5 tw-h-1.5 tw-bg-green-500 tw-rounded-full tw-flex-shrink-0"
                title="Online"
              ></div>
            </div>
            
            <p v-if="participant.role" class="tw-text-xs tw-text-gray-500 tw-truncate">
              {{ participant.role }}
            </p>
            <p v-else-if="participant.company" class="tw-text-xs tw-text-gray-500 tw-truncate">
              {{ participant.company }}
            </p>
            <p v-else class="tw-text-xs tw-text-gray-400 tw-truncate">
              Session participant
            </p>
          </div>
          
          <!-- Participant Actions -->
          <div class="tw-flex tw-items-center tw-gap-1">
            <!-- Message Button (for direct messaging) -->
            <button
              @click="sendDirectMessage(participant)"
              class="tw-p-0.5 tw-text-gray-400 hover:tw-text-primary-600 hover:tw-bg-primary-50 tw-rounded tw-transition-colors"
              title="Send direct message"
            >
              <icon icon="solar:chat-round-dots-bold-duotone" class="tw-text-xs" />
            </button>
            
            <!-- More Options -->
            <button
              @click="showParticipantOptions(participant)"
              class="tw-p-0.5 tw-text-gray-400 hover:tw-text-gray-600 hover:tw-bg-gray-100 tw-rounded tw-transition-colors"
              title="More options"
            >
              <icon icon="solar:menu-dots-bold-duotone" class="tw-text-xs" />
            </button>
          </div>
        </div>
      </div>

      <!-- Empty State -->
      <div v-else class="tw-flex tw-flex-col tw-items-center tw-justify-center tw-p-8 tw-text-center">
        <div class="tw-w-16 tw-h-16 tw-bg-gray-100 tw-rounded-full tw-flex tw-items-center tw-justify-center tw-mb-4">
          <icon icon="solar:users-group-rounded-bold-duotone" class="tw-text-2xl tw-text-gray-400" />
        </div>
        <h3 class="tw-text-lg tw-font-medium tw-text-gray-900 tw-mb-2">No participants yet</h3>
        <p class="tw-text-sm tw-text-gray-600">
          Participants will appear here once they join the session
        </p>
      </div>
    </div>

    <!-- Compact Session Actions Footer -->
    <div class="tw-p-2 tw-border-t tw-border-gray-200 tw-bg-white">
      <div class="tw-space-y-1">
        <!-- Invite Participants Button -->
        <button
          @click="inviteParticipants"
          class="tw-w-full tw-flex tw-items-center tw-justify-center tw-gap-1 tw-px-2 tw-py-1 tw-bg-primary-600 hover:tw-bg-primary-700 tw-text-white tw-rounded tw-transition-colors tw-text-xs tw-font-medium"
        >
          <icon icon="solar:user-plus-bold-duotone" class="tw-text-xs" />
          Invite
        </button>
        
        <!-- Session Settings Button -->
        <button
          @click="openSessionSettings"
          class="tw-w-full tw-flex tw-items-center tw-justify-center tw-gap-1 tw-px-2 tw-py-1 tw-bg-gray-100 hover:tw-bg-gray-200 tw-text-gray-700 tw-rounded tw-transition-colors tw-text-xs"
        >
          <icon icon="solar:settings-bold-duotone" class="tw-text-xs" />
          Settings
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { defineProps, defineEmits } from 'vue';
import moment from 'moment';

// Props
defineProps({
  participants: {
    type: Array,
    default: () => []
  },
  loading: {
    type: Boolean,
    default: false
  },
  session: {
    type: Object,
    default: null
  }
});

// Emits
const emit = defineEmits(['direct-message', 'invite-participants', 'session-settings']);

// Methods
const sendDirectMessage = (participant) => {
  emit('direct-message', participant);
};

const showParticipantOptions = (participant) => {
  // This would typically show a dropdown menu with options
  console.log('Show options for participant:', participant);
};

const inviteParticipants = () => {
  emit('invite-participants');
};

const openSessionSettings = () => {
  emit('session-settings');
};

const formatSessionDate = (date, time) => {
  if (!date) return '';
  const dateObj = moment(`${date} ${time}`, 'YYYY-MM-DD HH:mm');
  const today = moment();
  const tomorrow = moment().add(1, 'day');

  if (dateObj.isSame(today, 'day')) {
    return `Today ${dateObj.format('h:mm A')}`;
  } else if (dateObj.isSame(tomorrow, 'day')) {
    return `Tomorrow ${dateObj.format('h:mm A')}`;
  } else {
    return dateObj.format('MMM D, h:mm A');
  }
};

const formatDuration = (startTime, endTime) => {
  if (!startTime || !endTime) return '';
  const start = moment(startTime, 'HH:mm');
  const end = moment(endTime, 'HH:mm');
  const duration = moment.duration(end.diff(start));
  const hours = Math.floor(duration.asHours());
  const minutes = duration.minutes();
  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  }
  return `${minutes}m`;
};
</script>

<style scoped>
/* Custom scrollbar for participants list */
.tw-overflow-y-auto::-webkit-scrollbar {
  width: 4px;
}

.tw-overflow-y-auto::-webkit-scrollbar-track {
  background: transparent;
}

.tw-overflow-y-auto::-webkit-scrollbar-thumb {
  background: #d1d5db;
  border-radius: 2px;
}

.tw-overflow-y-auto::-webkit-scrollbar-thumb:hover {
  background: #9ca3af;
}

/* Smooth transitions */
.tw-transition-colors {
  transition: color 0.2s ease-in-out, background-color 0.2s ease-in-out;
}

/* Hover effects */
button:hover {
  transform: translateY(-1px);
}

button:active {
  transform: translateY(0);
}
</style>
